<template>
  <div>
    <h1>Cookie Policy</h1>
    <p>This document is meant to explain the types of cookies and other tracking technologies that Shopify may place on your device, either when you are visiting our web properties, or if you are visiting the storefront of a merchant who uses our platform to power their site.
    </p>
    <br/>
    <h2>What are cookies?</h2>
    <p>A cookie is a small amount of information that’s downloaded to your computer or device when you visit certain websites. We use a number of different cookies on the Shopify website, including strictly necessary, performance, advertising, and social media or content cookies. Cookies make your browsing experience better by allowing the website to remember your actions and preferences (such as login and region selection). This means you don’t have to re-enter this information each time you return to the site or browse from one page to another. Cookies also provide information on how people use the website, for instance whether it’s their first time visiting or if they are a frequent visitor. Read more about cookies (and other similar tracking technologies) and how we use the data collected through these technologies, in our <router-link style="color: var(--primary) !important;" :to="'/' + 'privacyPolicy/' + $i18n.locale">Privacy Policy</router-link>.</p>
    <br/>
    <h2>What cookies do we use and why?</h2>
    <p>Some cookies are necessary to allow you to browse our website, use its features, and access secure areas. The use of these cookies is essential for the website to work. For example, we use user-input cookies for the duration of a session to keep track of a user’s input when filling in forms that span several pages.</p>
    <p>We also use functional cookies to remember choices you’ve made or information you’ve provided, such as your username, language, or the region you are in. This allows us to tailor your website experience specifically to your preferences. For example, authentication cookies are functional cookies that are used for the duration of a session (or persistent, if you agree to the “remember me” function) to allow users to authenticate themselves on subsequent visits or to gain access to authorized content across pages. The functional cookies we use include:</p>
    
    <ul>
      <li>User-centric security cookies to detect authentication abuses for a limited persistent duration, like repeated failed login attempts. These cookies are set for the specific task of increasing thesecurity of the service.</li>
      <li>Multimedia content player session cookies (flash cookies) are used for the duration of a session to store technical data needed to play back video or audio content (e.g. image quality, network link speed, and buffering parameters).
      </li>
      <li>Load balancing session cookies are used for the duration of the session to identify the same server in the pool in order for the load balancer to redirect user requests appropriately.
      </li>
      <li>User interface customization persistent cookies are used to store a user’s preference regarding a service across web pages.
      </li>
    </ul>

    <p>Expar Store is dedicated to user experience and we use many tools to help us improve our website and our commerce platform. To this end, we use reporting and analytics cookies to collect information about how you use our website or our merchants’ storefronts, and how often. These cookies only gather information for statistical purposes and only use pseudonymous cookie identifiers that do not directly identify you. The performance cookies we use include:</p>
    <ul>
      <li>First party analytics cookies - We use these cookies to estimate the number of unique visitors, to improve our websites and our merchants’ websites, and to detect the most searched for words in search engines that lead to a webpage. These cookies are not used to target you with online marketing. We use these cookies to learn how our websites and our merchants’ websites are performing and make relevant improvements to improve your browsing experience.</li>
      <li>Third party analytics cookies - We also use Google Analytics and other third-party analytics providers listed below to help measure how users interact with our website content. These cookies “remember” what our users have done on previous pages and how they’ve interacted with the website. For more information on Google Analytics, visit <a class="link" href="https://developers.google.com/analytics/devguides/collection/analyticsjs/cookie-usage" target="_blank">Google’s information page</a> . For instructions on how opt out of Google Analytics, see below.</li>
    </ul>

    <p>Advertising cookies are used on our website to tailor marketing to you and your interests and provide you with a more personalized service in the future. These cookies remember that you visited our website and we may share this information with third-parties, such as advertisers. Although these cookies can track your device’s visits to our website and other sites, they typically cannot personally identify you. Without these cookies, the advertisements that you see may be less relevant and interesting to you. Read more about how companies use cookies to conduct targeted or retargeted advertising <a class="link" href="https://www.networkadvertising.org/understanding-online-advertising/" target="_blank">here</a>. We do not set advertising cookies through our merchants’ storefronts ourselves, though merchants may choose to do so independently.</p>
    <p>Finally, Social and Content cookies are placed by many social media plugins (for example the Facebook ’like’ button), and other tools meant to provide or improve the content on a website (for example services that allow the playing of video files, or that create comments sections). We integrate these modules into our platform to improve the experience of browsing and interacting with our websites. Please note that some of these third party services place cookies that are also used for things like behavioural advertising, analytics, and/or market research.</p>

    <h2>Merchant storefronts</h2>
    <p>When merchants use our platform to power their online stores, we place the following cookies for visitors of their stores:</p>
    <p><strong>Cookies Necessary for the Functioning of the Store:</strong></p>
    <table>
      <thead>
        <th>Name</th>
        <th>Function</th>
      </thead>
      <tbody>
        <tr>
          <td>_ab</td>
          <td>Used in connection with access to admin.</td>
        </tr>
        <tr>
          <td>_secure_session_id</td>
          <td>Used in connection with navigation through a storefront.</td>
        </tr>
        <tr>
          <td>Cart</td>
          <td>Used in connection with shopping cart.</td>
        </tr>
        <tr>
          <td>cart_currency</td>
          <td>Used in connection with shopping cart.</td>
        </tr>
        <tr>
          <td>cart_sig</td>
          <td>Used in connection with checkout.</td>
        </tr>
        <tr>
          <td>cart_ts</td>
          <td>Used in connection with checkout.</td>
        </tr>
        <tr>
          <td>cart_ver</td>
          <td>Used in connection with shopping cart.</td>
        </tr>
        <tr>
          <td>checkout</td>
          <td>Used in connection with checkout.</td>
        </tr>
        <tr>
          <td>checkout_token</td>
          <td>Used in connection with checkout.</td>
        </tr>
        <tr>
          <td>cookietest</td>
          <td>Used in connection with navigation through a storefront.</td>
        </tr>
        <tr>
          <td>master_device_id</td>
          <td>Used in connection with merchant login.</td>
        </tr>
        <tr>
          <td>previous_checkout_token</td>
          <td>Used in connection with checkout.</td>
        </tr>
        <tr>
          <td>previous_step</td>
          <td>Used in connection with checkout.</td>
        </tr>
        <tr>
          <td>remember_me</td>
          <td>Used in connection with checkout.</td>
        </tr>
        <tr>
          <td>Secret</td>
          <td>Used in connection with checkout.</td>
        </tr>
        <tr>
          <td>Secure_customer_sig	</td>
          <td>Used in connection with customer login..</td>
        </tr>
        <tr>
          <td>storefront_digest</td>
          <td>Used in connection with customer login..</td>
        </tr>
      </tbody>
    </table>

    <p><strong>Reporting and Analytics</strong></p>
    <table>
      <thead>
        <th>Name</th>
        <th>Function</th>
      </thead>
      <tbody>
        <tr>
          <td>_landing_page</td>
          <td>Track landing pages.</td>
        </tr>
        <tr>
          <td>_orig_referrer</td>
          <td>Track landing pages.</td>
        </tr>
      </tbody>
    </table>

    <h2>Expar websites</h2>
    <p>When visitors load Shopify’s websites, we generally place the following Shopify cookies:</p>
    <p><strong>Cookies Necessary for the Functioning of the Sites</strong></p>
    <table>
      <thead>
        <th>Name</th>
        <th>Function</th>
      </thead>
      <tbody>
        <tr>
          <td>_Brochure_session</td>
          <td>Used in connection with browsing through site.</td>
        </tr>
      </tbody>
    </table>
    <br/>

    <p><strong>Reporting and Analytics</strong></p>
    <table>
      <thead>
        <th>Name</th>
        <th>Function</th>
      </thead>
      <tbody>
        <tr>
          <td>_landing_page</td>
          <td>Tracks landing pages.</td>
        </tr>
        <tr>
          <td>_orig_referrer</td>
          <td>Track landing pages.</td>
        </tr>
      </tbody>
    </table>

    <p>Additionally, we use pixels and tags from the following third parties, which may in turn place cookies:</p>
    <p><strong>Reporting and Analytics</strong></p>
    <table>
      <thead>
        <th>Third Party</th>
        <th>Description	</th>
        <th>Privacy Policy</th>
      </thead>
      <tbody>
        <tr>
          <td>Crazy Egg</td>
          <td>We use Crazy Egg to help measure how users interact with our websites.</td>
          <td>https://www.crazyegg.com/privacy</td>
        </tr>
        <tr>
          <td>DC Analytics</td>
          <td>We use DC Analytics to help measure how users interact with our websites.</td>
          <td>https://dcanalytics.dcmn.com/privacy-policy</td>
        </tr>
        <tr>
          <td>Google Analytics</td>
          <td>We use Google Analytics to help measure how users interact with our websites.</td>
          <td>https://policies.google.com/privacy</td>
        </tr>
        <tr>
          <td>Hotjar</td>
          <td>We use Hotjar to help measure how users interact with our websites.</td>
          <td>https://www.hotjar.com/legal/policies/privacy</td>
        </tr>
        <tr>
          <td>LinkedIn Analytics</td>
          <td>We use LinkedIn Analytics to help measure how users interact with our websites.</td>
          <td>https://www.linkedin.com/legal/privacy-policy</td>
        </tr>
        <tr>
          <td>Optimizely</td>
          <td>We use Optimizely to help us test improvements or changes to our websites.</td>
          <td>https://www.optimizely.com/privacy/</td>
        </tr>
      </tbody>
    </table>
    <br/>

    <p><strong>Social Media & Content:</strong></p>
    <table>
      <thead>
        <th>Third Party</th>
        <th>Description	</th>
        <th>Privacy Policy</th>
      </thead>
      <tbody>
        <tr>
          <td>Facebook Connect	</td>
          <td>We use Facebook Connect to allow visitors to our website to interact with and share content via Facebook’s social media platform.</td>
          <td>https://www.facebook.com/policy.php</td>
        </tr>
      </tbody>
    </table>
    <br/>
    
    <h2>How long will cookies remain on my computer or mobile device?</h2>
    <p>The length of time that a cookie remains on your computer or mobile device depends on whether it is a “persistent” or “session” cookie. Session cookies last until you stop browsing and persistent cookies last until they expire or are deleted. Most of the cookies we use are persistent and will expire between 30 minutes and two years from the date they are downloaded to your device. See the section below on how to control cookies for more information on removing them before they expire.</p>
    <br/>

    <h2>How to control cookies?</h2>
    <p>You can control and manage cookies in various ways. Please keep in mind that removing or blocking cookies can negatively impact your user experience and parts of our website may no longer be fully accessible.</p>
    <p>Most browsers automatically accept cookies, but you can choose whether or not to accept cookies through your browser controls, often found in your browser’s “Tools” or “Preferences” menu. For more information on how to modify your browser settings or how to block, manage or filter cookies can be found in your browser’s help file or through such sites as: <a class="link" href="www.allaboutcookies.org." target="_blank">www.allaboutcookies.org</a>.</p>
    <p>Many of the third party advertising and other tracking services listed above offer you the opportunity to opt out of their tracking systems. You can read more about the information they collect and how to opt out through the privacy policy links listed above.</p>
    <br/>
    <hr/>
    <p>Last updated: March 19, 2021</p>
    <br/>

  </div>
</template>

<script>
export default {
    metaInfo() {
    return {
      title: this.$i18n.t('cookiePolicy1'),
    }
  },
  //   nuxtI18n: {
  //     paths: {
  //       sl: "/politika-zasebnosti", // -> mydomain.com/en/politika-zasebnosti
  //       en: "/privacy-policy", // -> mydomain.com/en/privacy-policy
  //     },
  //   },
};
</script>

<style scoped>
h1 {
  font-size: 3em;
  line-height: 1.5em;
  margin: 1em;
}
h2 {
  font-size: 2.4em;
  font-weight: 600;
  line-height: 1em;
  margin: 0.5em;
}
h3 {
  font-size: 1.7em;
  line-height: 0.8em;
  margin: 0.4em;
}
h4 {
  font-size: 1.4em;
  line-height: 0.6em;
  margin-top: 1.5em;
}
p {
  font-size: 1.3em;
  line-height: 1.5em;
  text-align: justify;
  margin-left: 10em;
  margin-right: 10em;
}
li {
  font-size: 1.3em;
  line-height: 1.5em;
  text-align: left;
  margin-left: 20em;
  margin-right: 10em;
  list-style-type: square;
}

th {
  font-size: 1.3em;
  line-height: 1.5em;
  text-align: left;
}
td {
  font-size: 0.6em;
  line-height: 1.5em;
  text-align: left;
}
table {
  margin-left: 15em;
}
.link {
  color: var(--primary) !important;
}

@media screen and (max-width: 680px) {
  .privacy {
    margin: 0;
  }
  h1 {
    font-size: 2.5em;
    line-height: 1em;
    margin: 1em;
  }
  h2 {
    font-size: 1.7em;
    font-weight: 600;
    line-height: 1.2em;
    margin: 0.4em;
  }
  h3 {
    font-size: 1.6em;
    line-height: 1.2em;
    margin: 0.4em;
  }
  h4 {
    font-size: 1.4em;
    line-height: 1em;
    margin-top: 1.5em;
  }
  p {
    font-size: 1em;
    line-height: 1.5em;
    text-align: justify;
    margin: 20px;
    word-wrap: break-word;
  }
  li {
    font-size: 1em;
    line-height: 1.5em;
    text-align: left;
    margin-left: 3em;
    margin-right: 1em;
    list-style-type: square;
  }

  th {
    font-size: 1em;
    line-height: 1em;
    text-align: left;
    padding-bottom: 1.5em;
  }
  td {
    font-size: 0.65em;
    line-height: 1.6em;
    text-align: left;
    padding-bottom: 1.5em;
  }
  table {
    margin: 1em;
  }
}
</style>
